import {
	BuildingOfficeIcon,
	CheckIcon,
	ChevronDownIcon,
	Cog8ToothIcon,
	DocumentCheckIcon,
	EnvelopeIcon,
	EyeIcon,
	EyeSlashIcon,
	MinusIcon,
} from "@heroicons/react/20/solid";
import UserConnectedButton from "./UserConnectedButton";
import { useState } from "react";
import { useMemo } from "react";
import { useEffect } from "react";
import Tooltip from "@/common/tooltip/Tooltip";
import {UserIcon} from "@heroicons/react/24/solid";

export function CompanyIDInput({ companyId, setCompanyId }) {
	return (
		<div className="relative form-material mr-2">
			<label>Company ID</label>
			<input
				className="form-input form-control"
				type="text"
				name="companyId"
				value={companyId}
				onChange={(e) => setCompanyId(e.target.value)}
			/>
			<BuildingOfficeIcon className="align-middle w-8 h-8 absolute top-3.5 right-2" />
		</div>
	);
}

export function EmailInput({ email, setEmail }) {
	return (
		<div className="relative form-material mr-2">
			<label>Email</label>
			<input
				className="form-input form-control"
				type="email"
				name="email"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
			/>
			<EnvelopeIcon className="align-middle w-8 h-8 absolute top-3.5 right-2" />
		</div>
	);
}

export function UsernameInput({ username, setUsername }) {
	return (
		<div className="relative form-material mr-2">
			<label>Username</label>
			<input
				className="form-input form-control"
				type="text"
				name="username"
				value={username}
				onChange={(e) => setUsername(e.target.value)}
			/>
			<UserIcon className="align-middle w-8 h-8 absolute top-3.5 right-2" />
		</div>
	);
}

export function PasswordInput({ password, setPassword }) {
	const [preview, setPreview] = useState(false);
	const PasswordIcon = preview ? EyeIcon : EyeSlashIcon;

	return (
		<div className="relative form-material">
			<label>Password</label>
			<input
				className="form-input form-control pr-8"
				type={preview ? "text" : "password"}
				name="password"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
			/>
			<PasswordIcon
				className="align-middle w-8 h-8 cursor-pointer absolute top-3.5 right-2 select-none"
				onClick={() => setPreview(!preview)}
			/>
		</div>
	);
}

export function Credentials({ functionality, connect, showCredentials }) {
	const [credentials, setCredentials] = useState({});
	const updateCredentials = (key, value) => setCredentials({ ...credentials, [key]: value });
	const disabled = useMemo(() => {
		const requiredCredentials = Object.keys({ ...functionality?.login });
		return requiredCredentials.some((key) => !credentials[key]);
	}, [JSON.stringify(credentials), JSON.stringify(functionality?.login)]);

	if (!showCredentials) return null;
	return (
		<div className="flex justify-end">
			{functionality?.login?.companyId ? (
				<CompanyIDInput
					companyId={credentials.companyId}
					setCompanyId={(value) => updateCredentials("companyId", value)}
				/>
			) : null}
			{functionality?.login?.email ? (
				<EmailInput
					email={credentials.email}
					setEmail={(value) => updateCredentials("email", value)}
				/>
			) : null}
			{functionality?.login?.username ? (
				<UsernameInput
					username={credentials.username}
					setUsername={(value) => updateCredentials("username", value)}
				/>
			) : null}
			{functionality?.login?.password ? (
				<PasswordInput
					password={credentials.password}
					setPassword={(value) => updateCredentials("password", value)}
				/>
			) : null}
			<button
				className="btn btn-primary"
				disabled={disabled}
				onClick={() => {
					connect(credentials);
				}}
			>
				Connect
			</button>
		</div>
	);
}

export function DisconnectButton({ connected, disconnect }) {
	if (!connected) return null;
	return (
		<button
			className="btn btn-default"
			onClick={disconnect}
		>
			<MinusIcon className="align-middle mr-2 h-8 w-8 cursor-pointer" />
			Disconnect
		</button>
	);
}

export function ConnectionButton({
	functionality,
	connected,
	setShowCredentials,
	showCredentials,
	needsVerification,
	status,
	needsReconnect,
	connect,
	statusMessages,
	verifyConnection,
	isComingSoon,
}) {
	const connectFunction = () => {
		if (functionality?.login && !showCredentials) {
			setShowCredentials();
		} else {
			connect();
		}
	};
	if (showCredentials) return null;
	if (isComingSoon) {
		return <button className="btn btn-default">Coming Soon</button>;
	}
	if (needsReconnect) {
		return (
			<button
				className="btn btn-primary"
				onClick={connectFunction}
			>
				<Tooltip
					content={statusMessages}
					isOpen={statusMessages?.length ? undefined : false}
					distance={13}
				>
					{status}: Reconnect
				</Tooltip>
			</button>
		);
	}
	if (needsVerification) {
		return (
			<button
				className="btn btn-primary"
				onClick={verifyConnection}
			>
				<DocumentCheckIcon className="align-middle mr-2 h-8 w-8" />
				Verify Connection
			</button>
		);
	}

	if (connected) {
		return (
			<button className="btn btn-default">
				<CheckIcon className="align-middle mr-2 h-8 w-8" />
				Connected
			</button>
		);
	}

	return (
		<button
			className="btn btn-primary"
			onClick={connectFunction}
		>
			Connect
		</button>
	);
}

export function ConnectionConfigButton({
	connected,
	needsVerification,
	needsReconnect,
	expand,
	setExpand,
	disconnect,
}) {
	if ((connected && !setExpand) || needsVerification || needsReconnect) {
		return (
			<DisconnectButton
				connected={connected}
				disconnect={disconnect}
			/>
		);
	}
	if (!connected) return null;
	return (
		<button
			className="btn btn-default"
			onClick={() => setExpand(!expand)}
		>
			<Cog8ToothIcon
				className="align-middle mr-2 w-8 h-8"
				style={{
					transform: expand ? "rotate(180deg)" : "rotate(0deg)",
					transition: "transform 0.5s ease-in-out",
				}}
			/>
			Settings
			<ChevronDownIcon
				className="align-middle ml-2 w-8 h-8"
				style={{
					transform: expand ? "rotateX(180deg)" : "rotate(0deg)",
					transition: "transform 0.3s ease-in-out",
				}}
			/>
		</button>
	);
}

export function IntegrationConnectionHandler({
	className = "btn-group pull-right",
	connected,
	userConnected,
	status,
	reconnect,
	connect,
	verifyConnection,
	statusMessages,
	disconnect,
	expand,
	setExpand,
	advisor,
	functionality,
	isComingSoon,
}) {
	const needsVerification = status === "Pending";
	const needsReconnect =
		["Expired", "Failed", "Invalid Credentials", "Revoked", "Unauthorized"].includes(status) || reconnect;
	const [showCredentials, setShowCredentials] = useState(false);

	useEffect(() => {
		if (connected && showCredentials) setShowCredentials(false);
	}, [connected, status]);

	return (
		<>
			<Credentials
				functionality={functionality}
				connect={connect}
				showCredentials={showCredentials}
			/>
			<div className={className}>
				<UserConnectedButton
					functionality={functionality}
					integration={userConnected}
					connectUser={!!userConnected?.userId}
					connectAdvisor={!!userConnected?.advisor}
					advisor={advisor}
				/>
				<ConnectionButton
					functionality={functionality}
					connected={connected}
					isComingSoon={isComingSoon}
					needsVerification={needsVerification}
					status={status}
					showCredentials={showCredentials}
					statusMessages={statusMessages}
					setShowCredentials={() => setShowCredentials(!showCredentials)}
					connect={connect}
					verifyConnection={verifyConnection}
					needsReconnect={needsReconnect}
				/>
				<ConnectionConfigButton
					functionality={functionality}
					connected={connected}
					disconnect={disconnect}
					expand={expand}
					status={status}
					needsVerification={needsVerification}
					needsReconnect={needsReconnect}
					setExpand={setExpand}
				/>
			</div>
		</>
	);
}
